import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Receipt, RotateCw, FileArchive } from "lucide-react";
import { getToken } from "@/lib/session";

// Default range = current year Jan → current month
const now = new Date();
const startDefault = `${now.getFullYear()}-01`;
const endDefault = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

export default function AdminTotReport() {
  const [from, setFrom] = useState(startDefault);
  const [to, setTo] = useState(endDefault);
  const [rate, setRate] = useState(0.05);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tot/report?from_month=${from}&to_month=${to}&rate=${rate}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };

  const downloadZip = async () => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = getToken() || "";
      const url = `${backend}/api/finance/accountant-export?from_month=${from}&to_month=${to}&rate=${rate}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const filename = `powerbuilt_accountant_${from}_to_${to}.zip`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export ready");
    } catch (e) {
      toast.error("Export failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cls = "border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <div data-testid="admin-tot-report" className="space-y-6">
      <div>
        <p className="overline">Finance · Sint Maarten</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
          Turnover Tax (TOT) Report
        </h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
          Monthly TOT owed based on recorded revenue (5% default). Voided entries excluded.
        </p>
      </div>

      <div className="card-pb p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8] block">From</label>
            <input type="month" className={cls} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="tot-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8] block">To</label>
            <input type="month" className={cls} value={to} onChange={(e) => setTo(e.target.value)} data-testid="tot-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8] block">Rate</label>
            <select className={cls} value={rate} onChange={(e) => setRate(Number(e.target.value))} data-testid="tot-rate">
              <option value={0.05}>5% (SXM default)</option>
              <option value={0.03}>3%</option>
              <option value={0.07}>7%</option>
              <option value={0.10}>10%</option>
            </select>
          </div>
          <button onClick={load} disabled={loading} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="tot-refresh">
            <RotateCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Compute
          </button>
          <button onClick={downloadZip} className="btn-outline-navy text-sm inline-flex items-center gap-1" data-testid="tot-download-zip">
            <FileArchive className="w-3 h-3" /> Accountant ZIP
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Taxable base" value={`$${data.totals.taxable_base.toLocaleString()}`} />
            <StatCard label="TOT owed" value={`$${data.totals.tax_owed.toLocaleString()}`} accent />
            <StatCard label="Entries" value={data.totals.entry_count} />
          </div>

          <div className="card-pb overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
                <tr>
                  <th className="text-left px-4 py-2">Month</th>
                  <th className="text-right px-4 py-2">Taxable base</th>
                  <th className="text-right px-4 py-2">Tax @ {(data.rate * 100).toFixed(1)}%</th>
                  <th className="text-right px-4 py-2">Entries</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.month} data-testid={`tot-row-${row.month}`}
                      className="border-t border-[#E2E8F0] dark:border-slate-700">
                    <td className="px-4 py-2 inline-flex items-center gap-1.5 text-[#0A2540] dark:text-white">
                      <Receipt className="w-3 h-3 text-[#C9A356]" /> {row.month}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">${row.taxable_base.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">${row.tax_owed.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-[#94A3B8]">{row.entry_count}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#0A2540] dark:border-white bg-[#F8FAFC] dark:bg-slate-800">
                  <td className="px-4 py-2 font-bold text-[#0A2540] dark:text-white">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">${data.totals.taxable_base.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-[#C9A356]">${data.totals.tax_owed.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right font-bold text-[#94A3B8]">{data.totals.entry_count}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`card-pb p-5 ${accent ? "border-l-4 border-[#C9A356]" : ""}`} data-testid={`tot-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</div>
      <div className={`text-2xl font-heading mt-2 ${accent ? "text-[#C9A356]" : "text-[#0A2540] dark:text-white"}`}>
        {value}
      </div>
    </div>
  );
}
